import { chain, findKey, isEmpty, isFunction, result, size } from 'underscore';
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Separator } from '@fluentui/react';
import { SuggestionSectionCollapse } from '@bingads-webui-campaign-react/pill-item-components';
import RecommendationsSectionIcon from './media/recommendations-section.svg';
import { useCircularBufferView } from './use-circular-buffer-view';
import { TextAssetRecommendationsWithThemesPanel } from './text-asset-recommendations-panel';

export const MAX_INLINE_RECOMMENDATIONS_PER_PAGE = 5;
export const MAX_DKI_INLINE_RECOMMENDATIONS_PER_PAGE = 1;

const keyIsEmpty = ([text]) => isEmpty(text);

export const InlineRecommendations = ({
  i18n, collection, maxCollectionSize, availableRecommendations, singleAssetValidationPropsField,
  inlineRecommendationsHelpTopicKey, inlineRecommendationsHelpTopicAriaLabel, inlineRecommendationsTitle,
  inlineRecommendationsDisabledItemTooltip, applyRecommendation, applyRecommendations,
  reportDisplayedRecommendations, enableDKI, DKIThemeName,
}) => {
  const flattenedAvailableRecommendations = useMemo(() => chain(availableRecommendations)
    .map(theme => result(theme, 'assets'))
    .flatten()
    .compact()
    .uniq() // dedup
    .value(), [availableRecommendations]);

  const flattenedAllAvailableRecommendations = useMemo(() => {
    if (enableDKI) {
      const flattenedAvailableDKIRecommendations = chain(availableRecommendations.filter(theme => theme.name === DKIThemeName))
        .map(theme => result(theme, 'assets'))
        .flatten()
        .compact()
        .uniq() // dedup
        .value();

      const flattenedAvailableNoDKIRecommendations = chain(availableRecommendations.filter(theme => theme.name !== DKIThemeName))
        .map(theme => result(theme, 'assets'))
        .flatten()
        .compact()
        .uniq() // dedup
        .value();
      return [...flattenedAvailableDKIRecommendations.slice(0, MAX_DKI_INLINE_RECOMMENDATIONS_PER_PAGE), ...flattenedAvailableNoDKIRecommendations];
    }
    return flattenedAvailableRecommendations;
  }, [DKIThemeName, availableRecommendations, enableDKI, flattenedAvailableRecommendations]);

  const [availableInlineRecommendations, canMoveNext, moveNext] = useCircularBufferView(flattenedAllAvailableRecommendations, MAX_INLINE_RECOMMENDATIONS_PER_PAGE);

  useEffect(() => {
    if (!isEmpty(availableInlineRecommendations) && isFunction(reportDisplayedRecommendations)) {
      reportDisplayedRecommendations(availableInlineRecommendations);
    }
  }, [availableInlineRecommendations, reportDisplayedRecommendations]);

  let firstAvailableKey = findKey(collection, keyIsEmpty);
  if (!firstAvailableKey && size(collection) < maxCollectionSize) {
    firstAvailableKey = `${singleAssetValidationPropsField}${size(collection)}`;
  }

  const [prevAvailbleKeyWasDefined, setPrevAvailbleKeyWasDefined] = useState(!!firstAvailableKey);
  const [shouldForceCloseCollapse, setShouldForceCloseCollapse] = useState(!firstAvailableKey);
  useEffect(() => {
    const firstAvailableKeyIsDefined = !!firstAvailableKey;
    if (prevAvailbleKeyWasDefined !== firstAvailableKeyIsDefined) {
      if (!shouldForceCloseCollapse && (prevAvailbleKeyWasDefined && !firstAvailableKeyIsDefined)) {
        setShouldForceCloseCollapse(true);
      } else if (shouldForceCloseCollapse && (!prevAvailbleKeyWasDefined && firstAvailableKeyIsDefined)) {
        setShouldForceCloseCollapse(false);
      }
      setPrevAvailbleKeyWasDefined(firstAvailableKeyIsDefined);
    }
  }, [firstAvailableKey, prevAvailbleKeyWasDefined, shouldForceCloseCollapse]);

  const onAddSuggestedItem = useCallback(
    (itemIndex, itemText) => firstAvailableKey && applyRecommendation(firstAvailableKey, itemText),
    [firstAvailableKey, applyRecommendation]
  );

  const onAddAllSuggestedItems = useCallback(
    () => applyRecommendations(availableInlineRecommendations),
    [availableInlineRecommendations, applyRecommendations]
  );

  const [isSuggestionsPanelOpen, setIsSuggestionsPanelOpen] = useState(false);
  const onMoreSuggestionsButtonClick = useCallback(() => {
    setIsSuggestionsPanelOpen(true);
    reportDisplayedRecommendations(flattenedAvailableRecommendations);
  }, [flattenedAvailableRecommendations, reportDisplayedRecommendations]);

  const onCancelAddingMultipleRecommendations = useCallback(
    () => setIsSuggestionsPanelOpen(false),
    []
  );

  const onAddMultipleRecommendations = useCallback(
    (recommendations) => {
      applyRecommendations(recommendations);
      setIsSuggestionsPanelOpen(false);
    },
    [applyRecommendations]
  );

  if (!isEmpty(availableInlineRecommendations)) {
    return (
      <>
        <Separator styles={{ root: { marginBottom: 6 } }} />
        <SuggestionSectionCollapse
          className="rsa-suggestion-section-collapse"
          topicKey={inlineRecommendationsHelpTopicKey}
          topicAriaLabel={i18n.getString(inlineRecommendationsHelpTopicAriaLabel)}
          icon={RecommendationsSectionIcon}
          iconAltText={inlineRecommendationsTitle}
          suggestedItems={availableInlineRecommendations.map(itemText => ({ itemText, key: itemText }))}
          onAddSuggestedItem={onAddSuggestedItem}
          onAddAllSuggestedItems={onAddAllSuggestedItems}
          suggestedSectionTitle={i18n.getString(inlineRecommendationsTitle)}
          showSuggestionsSectionShimmer={false}
          showMoreSuggestionsInlineButton={canMoveNext}
          onMoreSuggestionsInlineButtonClick={moveNext}
          showAddAllSuggestionsButton={!!firstAvailableKey}
          addAllSuggestionsButtonText={i18n.getString(_TL_('Add all'))}
          showMoreSuggestionsButton={canMoveNext}
          moreSuggestionsButtonText={i18n.getString(_TL_('View more'))}
          disableAllSuggestions={!firstAvailableKey}
          disabledSuggestionTooltip={i18n.getString(inlineRecommendationsDisabledItemTooltip)}
          useStyleFluent2
          onMoreSuggestionsButtonClick={onMoreSuggestionsButtonClick}
          forceClose={shouldForceCloseCollapse}
        />
        {isSuggestionsPanelOpen &&
          <TextAssetRecommendationsWithThemesPanel
            i18n={i18n}
            collection={collection}
            availableRecommendations={availableRecommendations}
            allRecommendations={flattenedAvailableRecommendations}
            maxCollectionSize={maxCollectionSize}
            title={i18n.getString(inlineRecommendationsTitle)}
            topicKey={inlineRecommendationsHelpTopicKey}
            topicAriaLabel={i18n.getString(inlineRecommendationsHelpTopicAriaLabel)}
            onSave={onAddMultipleRecommendations}
            onCancel={onCancelAddingMultipleRecommendations}
            disabledSuggestionTooltip={i18n.getString(inlineRecommendationsDisabledItemTooltip)}
            reportDisplayedRecommendations={reportDisplayedRecommendations}
            DKIThemeName={DKIThemeName}
          />
        }
      </>
    );
  }

  return null;
};

InlineRecommendations.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  collection: PropTypes.objectOf(PropTypes.string).isRequired,
  maxCollectionSize: PropTypes.number.isRequired,
  availableRecommendations: PropTypes.arrayOf(PropTypes.string).isRequired,
  singleAssetValidationPropsField: PropTypes.string.isRequired,
  inlineRecommendationsHelpTopicKey: PropTypes.string.isRequired,
  inlineRecommendationsHelpTopicAriaLabel: PropTypes.string.isRequired,
  inlineRecommendationsTitle: PropTypes.string.isRequired,
  inlineRecommendationsDisabledItemTooltip: PropTypes.string.isRequired,
  applyRecommendation: PropTypes.func.isRequired,
  applyRecommendations: PropTypes.func.isRequired,
  reportDisplayedRecommendations: PropTypes.func,
  enableDKI: PropTypes.bool,
  DKIThemeName: PropTypes.string,
};

InlineRecommendations.defaultProps = {
  reportDisplayedRecommendations: undefined,
  enableDKI: false,
  DKIThemeName: '',
};
